import React, { useCallback, useState, useEffect, useRef, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Music2, LogOut, User, Instagram, Trash2, CalendarHeart, Heart, Sparkles, History, Building2, Wallet, Phone, Palette, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { fetchAppreciation } from "../games/favoriteDances";
import { useAuth } from "../lib/auth";
import { useGuestIdentity } from "../lib/guestIdentity";
import { useLocale } from "../lib/locale";
import { emptyPaymentFields } from "../lib/payment";
import { emptyMiniProfile, miniProfileFromApi, miniProfilePayload } from "../lib/miniProfile";
import { eventPublicPath } from "../lib/eventLinks";
import { attendanceLabel } from "../lib/rsvp";
import {
  HOST_SECTION,
  HOST_SECTION_IDS,
  HOST_SECTION_PANEL_IDS,
  PROFILE_PANEL_IDS,
  PROFILE_TAB,
  PROFILE_TAB_IDS,
  isHostSettingsSection,
  isThemeSection,
  normalizeProfileSearch,
  profileTabOrder,
  resolveInitialHostSection,
  resolveInitialProfileTab,
} from "../lib/profileTabs";
import SelfPaymentForm from "../components/SelfPaymentForm";
import CityAutocomplete from "../components/CityAutocomplete";
import InstagramInput from "../components/InstagramInput";
import ThumbnailUploadField from "../components/ThumbnailUploadField";
import { instagramHandleForInput, normalizeInstagramUrl } from "../lib/instagram";
import { normalizePhone } from "../lib/phone";
import { resolveProfilePhone, accountLinkedDancerId } from "../lib/dancerProfile";
import { Button, Card, EmptyState } from "../components/ui";
import FormLabel from "../components/ui/FormLabel";
import FormTextField from "../components/ui/FormTextField";
import DanceRoleField from "../components/DanceRoleField";
import DanceFormsField from "../components/DanceFormsField";
import ContactPhoneField from "../components/ContactPhoneField";
import {
  CheckboxField,
  FieldHint,
  FORM_STYLE_CARD_CLASS,
  FormValidationAlert,
  SectionTitle,
} from "../components/ui/FormSection";
import ProfileStickySaveBar, {
  PROFILE_HOST_FORM_ID,
  PROFILE_PERSONAL_FORM_ID,
} from "../components/ProfileStickySaveBar";
import ProfileLoadingSkeleton from "../components/profile/ProfileLoadingSkeleton";
import ProfileOnboardingWizard from "../components/profile/ProfileOnboardingWizard";
import ProfileHostEventsPanel from "../components/profile/ProfileHostEventsPanel";
import NotificationSettings from "../components/notifications/NotificationSettings";
import SchoolNotificationsSection from "../components/schools/SchoolNotificationsSection";
import HostSchoolSetup from "../components/schools/HostSchoolSetup";
import SchoolProfileForm from "../components/schools/SchoolProfileForm";
import ConfirmLeaveModal from "../components/ConfirmLeaveModal";
import { readableTabClass } from "../components/ui/TabButton";
import {
  PROFILE_FIELD_IDS,
  PROFILE_HOST_FIELD_ORDER,
  personalFieldsPendingHydration,
  scrollToFirstProfileFieldError,
  serializeHostSnapshot,
  serializePersonalSnapshot,
  validatePersonalForm,
  validateHostForm,
  validateHostPayment,
} from "../lib/profileForm";
import {
  DEFAULT_HOST_THEME,
  HOST_THEME_IDS,
  HOST_THEMES,
  normalizeHostTheme,
} from "../lib/hostThemes";
import ThemePreviewDrawer from "../components/profile/ThemePreviewDrawer";

const PROFILE_LIST_LINK =
  "block rounded-md2 border border-black/[0.08] bg-white px-4 py-3 min-h-[44px] flex items-center justify-between gap-3 font-ui font-semibold text-[14px] hover:bg-cream active:bg-cream/70 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-orchid/45 focus-visible:outline-offset-2";

const HOST_SCHOOL_ERROR_KEYS = new Set(["school_name", "school_city"]);

function newDancerId() {
  const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
  return Array.from({ length: 12 }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
}

function hostSectionForErrors(errors) {
  for (const key of PROFILE_HOST_FIELD_ORDER) {
    if (!errors[key]) continue;
    return HOST_SCHOOL_ERROR_KEYS.has(key) ? HOST_SECTION.school : HOST_SECTION.payment;
  }
  return HOST_SECTION.payment;
}

function ProfileTab({ active, onClick, testId, tabId, panelId, disabled = false, className = "", children }) {
  return (
    <button
      type="button"
      role="tab"
      id={tabId}
      aria-selected={active}
      aria-controls={panelId}
      aria-disabled={disabled || undefined}
      disabled={disabled}
      tabIndex={disabled ? -1 : active ? 0 : -1}
      onClick={disabled ? undefined : onClick}
      className={`${readableTabClass(active, { disabled })} ${className}`.trim()}
      data-testid={testId}
    >
      {children}
    </button>
  );
}


export default function ProfilePage({ defaultTab = "personal" }) {
  const { account, checking, updateAccount, signOut } = useAuth();
  const { dancerId, profile: guestProfile, revision, bindDancerId, clearGuest, refreshProfile } = useGuestIdentity();
  const locale = useLocale();
  const nav = useNavigate();
  const [params, setSearchParams] = useSearchParams();
  const onboarding = params.get("onboarding") === "1";
  const tabParam = params.get("tab");
  const sectionParam = params.get("section");

  const [tab, setTab] = useState(() => resolveInitialProfileTab(defaultTab, tabParam, onboarding));
  const [hostSection, setHostSection] = useState(() =>
    resolveInitialHostSection(sectionParam, tabParam),
  );
  const [themeDraft, setThemeDraft] = useState(() =>
    normalizeHostTheme(account?.theme || DEFAULT_HOST_THEME),
  );
  const [savingTheme, setSavingTheme] = useState(false);
  const [savingHostsPaid, setSavingHostsPaid] = useState(false);
  const [savingRunsSchool, setSavingRunsSchool] = useState(false);
  const [themePreviewOpen, setThemePreviewOpen] = useState(false);

  const [form, setForm] = useState({
    name: "",
    contact_phone: "",
    base_city: "",
    base_city_location: null,
    personal_instagram: "",
    hosts_paid: false,
    runs_school: false,
    school_name: "",
    school_city: "",
    school_city_location: null,
    school_bio: "",
    school_instagram: "",
    school_whatsapp_url: "",
  });
  const [defaultPayment, setDefaultPayment] = useState(() => emptyPaymentFields(null));
  const [danceForm, setDanceForm] = useState({ role: "", dance_forms: [] });
  const [miniProfile, setMiniProfile] = useState(() => emptyMiniProfile());
  const [showMiniOnEvents, setShowMiniOnEvents] = useState(false);
  const [loadingDance, setLoadingDance] = useState(true);
  const [savingPersonal, setSavingPersonal] = useState(false);
  const [savingHost, setSavingHost] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [profileEvents, setProfileEvents] = useState([]);
  const [loadingProfileEvents, setLoadingProfileEvents] = useState(false);
  const [pastEvents, setPastEvents] = useState([]);
  const [loadingPastEvents, setLoadingPastEvents] = useState(false);
  const [appreciation, setAppreciation] = useState(null);
  const [, setLoadingAppreciation] = useState(false);
  const [personalBaseline, setPersonalBaseline] = useState(null);
  const [hostBaseline, setHostBaseline] = useState(null);
  const [personalFieldErrors, setPersonalFieldErrors] = useState({});
  const [hostFieldErrors, setHostFieldErrors] = useState({});
  const [pendingLeave, setPendingLeave] = useState(null);
  /** False until the user edits personal fields — keeps baseline in sync with hydration/auto-resolve. */
  const personalTouchedRef = useRef(false);

  const personalSnapshot = useMemo(
    () => serializePersonalSnapshot({ form, danceForm, miniProfile, showMiniOnEvents, account }),
    [form, danceForm, miniProfile, showMiniOnEvents, account]
  );
  const hostSnapshot = useMemo(
    () => serializeHostSnapshot({ form, defaultPayment }),
    [form, defaultPayment]
  );
  const personalDirty =
    personalTouchedRef.current &&
    personalBaseline !== null &&
    personalSnapshot !== personalBaseline;
  const hostDirty = hostBaseline !== null && hostSnapshot !== hostBaseline;
  const themeDirty =
    !!account && normalizeHostTheme(themeDraft) !== normalizeHostTheme(account.theme);
  const hostTabDirty = isThemeSection(hostSection) ? themeDirty : isHostSettingsSection(hostSection) ? hostDirty : false;
  const tabHasUnsavedEdits =
    (tab === PROFILE_TAB.personal && personalDirty)
    || (tab === PROFILE_TAB.host && hostTabDirty);

  const tabOrder = useMemo(() => profileTabOrder({ account }), [account]);

  const tabListRef = useRef(null);
  const focusAfterTabRef = useRef(null);

  useEffect(() => {
    if (onboarding) {
      setTab(PROFILE_TAB.personal);
      return;
    }

    const next = normalizeProfileSearch(tabParam, sectionParam, { defaultTab });
    setTab(next.tab);
    if (next.tab === PROFILE_TAB.host && next.section) {
      setHostSection(next.section);
    }
    if (next.rewrite) {
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev);
        if (next.tab === PROFILE_TAB.personal) p.delete("tab");
        else p.set("tab", next.tab);
        if (next.tab === PROFILE_TAB.host && next.section && next.section !== HOST_SECTION.events) {
          p.set("section", next.section);
        } else {
          p.delete("section");
        }
        // Default events section omits section param for cleaner /me?tab=host
        if (next.tab === PROFILE_TAB.host && next.section === HOST_SECTION.events) {
          p.set("tab", "host");
          p.delete("section");
        }
        return p;
      }, { replace: true });
    }
  }, [defaultTab, tabParam, sectionParam, onboarding, setSearchParams]);

  useEffect(() => {
    if (account?.theme) setThemeDraft(normalizeHostTheme(account.theme));
  }, [account?.theme]);

  useEffect(() => {
    if (checking) return;
    if (onboarding && account === false) {
      nav("/login?next=/me");
      return;
    }
    if (account) {
      const nextForm = {
        name: account.name || "",
        base_city: account.base_city || account.school_city || "",
        base_city_location: account.base_city_location || account.school_city_location || null,
        personal_instagram: instagramHandleForInput(account.personal_instagram),
        hosts_paid: !!account.hosts_paid,
        runs_school: !!account.runs_school,
        school_name: account.school_name || "",
        school_city: account.school_city || "",
        school_city_location: account.school_city_location || null,
        school_bio: account.school_bio || "",
        school_instagram: instagramHandleForInput(account.school_instagram),
        school_whatsapp_url: "",
      };
      const nextPayment = emptyPaymentFields(account);
      // Preserve phone so account reloads don't wipe hydration / user input before baseline sync.
      setForm((f) => ({ ...nextForm, contact_phone: f.contact_phone || "" }));
      setDefaultPayment(nextPayment);
      setHostBaseline(serializeHostSnapshot({ form: nextForm, defaultPayment: nextPayment }));
    }
  }, [account, checking, nav, onboarding]);

  useEffect(() => {
    if (!account || locale.loading || !locale.default_city) return;
    setForm((f) => {
      if (f.base_city?.trim()) return f;
      return { ...f, base_city: locale.default_city, base_city_location: null };
    });
  }, [account, locale.loading, locale.default_city]);

  useEffect(() => {
    if (account) return;
    const guestName = guestProfile?.name?.trim();
    if (!guestName) return;
    setForm((f) => (f.name?.trim() ? f : { ...f, name: guestName }));
  }, [guestProfile?.name, account]);

  useEffect(() => {
    const phone = resolveProfilePhone({ profile: guestProfile, account });
    if (!phone) return;
    setForm((f) => (f.contact_phone?.trim() ? f : { ...f, contact_phone: phone }));
  }, [guestProfile?.phone, account?.phone, account?.default_payment?.contact_phone]);

  useEffect(() => {
    if (!dancerId) {
      setDanceForm({ role: "", dance_forms: [] });
      setMiniProfile(emptyMiniProfile());
      setShowMiniOnEvents(false);
      setLoadingDance(false);
      return;
    }
    if (guestProfile?.id === dancerId) {
      setDanceForm({
        role: guestProfile.role || "",
        dance_forms: guestProfile.dance_forms || [],
      });
      setMiniProfile(miniProfileFromApi(guestProfile.mini_profile));
      setShowMiniOnEvents(!!guestProfile.show_mini_on_events);
      if (guestProfile.phone) {
        setForm((f) => (f.contact_phone?.trim() ? f : { ...f, contact_phone: guestProfile.phone }));
      }
      setLoadingDance(false);
      return;
    }
    let cancelled = false;
    setLoadingDance(true);
    (async () => {
      try {
        const r = await api.get(`/dancers/${dancerId}`);
        if (cancelled) return;
        setDanceForm({
          role: r.data.role || "",
          dance_forms: r.data.dance_forms || [],
        });
        setMiniProfile(miniProfileFromApi(r.data.mini_profile));
        setShowMiniOnEvents(!!r.data.show_mini_on_events);
        if (r.data.phone) {
          setForm((f) => (f.contact_phone?.trim() ? f : { ...f, contact_phone: r.data.phone }));
        }
      } catch {
        // Profile may not exist yet
      } finally {
        if (!cancelled) setLoadingDance(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dancerId, guestProfile, revision]);

  useEffect(() => {
    if (!dancerId) {
      setProfileEvents([]);
      return;
    }
    let cancelled = false;
    setLoadingProfileEvents(true);
    (async () => {
      try {
        const r = await api.get(`/dancers/${dancerId}/profile-events`);
        if (!cancelled) setProfileEvents(r.data.events || []);
      } catch {
        if (!cancelled) setProfileEvents([]);
      } finally {
        if (!cancelled) setLoadingProfileEvents(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dancerId]);

  useEffect(() => {
    if (!dancerId) {
      setPastEvents([]);
      return;
    }
    let cancelled = false;
    setLoadingPastEvents(true);
    (async () => {
      try {
        const r = await api.get(`/dancers/${dancerId}/past-events`);
        if (!cancelled) setPastEvents(r.data.events || []);
      } catch {
        if (!cancelled) setPastEvents([]);
      } finally {
        if (!cancelled) setLoadingPastEvents(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dancerId, revision]);

  const personalHydrationPending =
    checking ||
    loadingDance ||
    personalFieldsPendingHydration({
      form,
      account,
      localeLoading: locale.loading,
      localeDefaultCity: locale.default_city,
      guestPhone: guestProfile?.phone,
      organizerPhone: account?.phone || account?.default_payment?.contact_phone,
      guestName: guestProfile?.name,
    });

  useEffect(() => {
    personalTouchedRef.current = false;
  }, [dancerId, account?.id]);

  useEffect(() => {
    // Wait until locale/phone/name hydration has landed in `form`, otherwise the
    // baseline is captured too early and the Personal tab looks falsely dirty.
    // Keep re-syncing until the user edits — CityAutocomplete resolve, phone/name
    // fills, and locale default city must not count as unsaved edits.
    if (personalTouchedRef.current) return;
    if (personalHydrationPending) {
      setPersonalBaseline(null);
      return;
    }
    setPersonalBaseline(personalSnapshot);
  }, [personalHydrationPending, personalSnapshot]);

  useEffect(() => {
    if (!personalDirty && !hostDirty && !themeDirty) return;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [personalDirty, hostDirty, themeDirty]);

  useEffect(() => {
    if (!dancerId) {
      setAppreciation(null);
      setLoadingAppreciation(false);
      return;
    }
    let cancelled = false;
    setLoadingAppreciation(true);
    (async () => {
      try {
        const data = await fetchAppreciation(dancerId);
        if (!cancelled) setAppreciation(data?.hidden ? null : data);
      } catch {
        if (!cancelled) setAppreciation(null);
      } finally {
        if (!cancelled) setLoadingAppreciation(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dancerId, revision]);

  useEffect(() => {
    if (!focusAfterTabRef.current) return;
    const targetTab = focusAfterTabRef.current;
    focusAfterTabRef.current = null;
    requestAnimationFrame(() => {
      document.getElementById(PROFILE_TAB_IDS[targetTab])?.focus();
    });
  }, [tab]);

  const updateBaseCityLocation = useCallback((city) => {
    setForm((f) => ({ ...f, base_city_location: city }));
    if (city) {
      setPersonalFieldErrors((prev) => {
        if (!prev.base_city) return prev;
        const next = { ...prev };
        delete next.base_city;
        return next;
      });
    }
  }, []);

  if (checking || (onboarding && !account)) {
    return (
      <div className="min-h-screen bg-canvas max-w-page mx-auto px-4 pt-6 pb-8">
        <ProfileLoadingSkeleton label="Loading profile…" variant="page" />
      </div>
    );
  }

  function switchTab(next) {
    if (next !== PROFILE_TAB.host) setThemePreviewOpen(false);
    setTab(next);
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (next === PROFILE_TAB.personal) p.delete("tab");
      else p.set("tab", next);
      if (next === PROFILE_TAB.host) {
        if (hostSection === HOST_SECTION.events) p.delete("section");
        else p.set("section", hostSection);
      } else {
        p.delete("section");
      }
      return p;
    }, { replace: true });
  }

  function switchHostSection(next) {
    if (next === hostSection) return;
    if (next !== HOST_SECTION.theme) setThemePreviewOpen(false);
    setHostSection(next);
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set("tab", "host");
      if (next === HOST_SECTION.events) p.delete("section");
      else p.set("section", next);
      return p;
    }, { replace: true });
  }

  async function saveActiveHostTab() {
    return isThemeSection(hostSection) ? saveTheme() : saveHost();
  }

  function requestTabSwitch(next) {
    if (next === tab) return;
    if (tab === PROFILE_TAB.personal && personalDirty) {
      focusAfterTabRef.current = null;
      setPendingLeave({ kind: "tab", target: next });
      return;
    }
    if (tab === PROFILE_TAB.host && hostTabDirty) {
      focusAfterTabRef.current = null;
      setPendingLeave({ kind: "tab", target: next });
      return;
    }
    switchTab(next);
  }

  function requestHostSectionSwitch(next) {
    if (next === hostSection) return;
    if (tab === PROFILE_TAB.host && hostTabDirty) {
      focusAfterTabRef.current = null;
      setPendingLeave({ kind: "host-section", target: next });
      return;
    }
    switchHostSection(next);
  }

  function openDanceNightsTab() {
    if (tab === PROFILE_TAB.nights) return;
    focusAfterTabRef.current = PROFILE_TAB.nights;
    requestTabSwitch(PROFILE_TAB.nights);
  }

  function onProfileTabListKeyDown(e) {
    const idx = tabOrder.indexOf(tab);
    if (idx < 0) return;
    let nextIdx = null;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      nextIdx = (idx + 1) % tabOrder.length;
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      nextIdx = (idx - 1 + tabOrder.length) % tabOrder.length;
    } else if (e.key === "Home") {
      nextIdx = 0;
    } else if (e.key === "End") {
      nextIdx = tabOrder.length - 1;
    }
    if (nextIdx === null || nextIdx === idx) return;
    e.preventDefault();
    const next = tabOrder[nextIdx];
    focusAfterTabRef.current = next;
    requestTabSwitch(next);
  }

  /** App/browser back: show leave confirm; default action is discard. */
  function requestLeaveBack(to) {
    if (tabHasUnsavedEdits) {
      setPendingLeave({ kind: "route", to });
      return;
    }
    nav(to);
  }

  /** In-app link: save silently, then navigate — no confirm modal. */
  async function leaveViaLink(to) {
    if (!tabHasUnsavedEdits) {
      nav(to);
      return;
    }
    const ok = tab === PROFILE_TAB.host
      ? await saveActiveHostTab()
      : await savePersonal();
    if (!ok) return;
    nav(to);
  }

  function requestSignOut() {
    if (tabHasUnsavedEdits) {
      setPendingLeave({ kind: "signout" });
      return;
    }
    signOut();
    clearGuest();
    toast("Signed out.");
    nav("/");
  }

  function confirmDiscardLeave() {
    if (!pendingLeave) return;
    if (pendingLeave.kind === "tab") {
      switchTab(pendingLeave.target);
    } else if (pendingLeave.kind === "host-section") {
      switchHostSection(pendingLeave.target);
    } else if (pendingLeave.kind === "route") {
      nav(pendingLeave.to);
    } else if (pendingLeave.kind === "signout") {
      signOut();
      clearGuest();
      toast("Signed out.");
      nav("/");
    }
    setPendingLeave(null);
  }

  async function confirmLeave({ save }) {
    if (!pendingLeave) return;
    if (save) {
      const ok = tab === PROFILE_TAB.host
        ? await saveActiveHostTab()
        : await savePersonal();
      if (!ok) {
        setPendingLeave(null);
        return;
      }
    }
    confirmDiscardLeave();
  }

  function clearHostFieldError(key) {
    if (!key) return;
    setHostFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  function clearPersonalFieldError(key) {
    if (!key) return;
    setPersonalFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  function markPersonalTouched() {
    if (personalTouchedRef.current) return;
    personalTouchedRef.current = true;
    // Freeze the pre-edit snapshot so hydration that lands after the first keystroke
    // does not wipe dirty state, and auto-fills before this point are not "edits".
    setPersonalBaseline(personalSnapshot);
  }

  function update(k, v) {
    if (
      k === "name" ||
      k === "base_city" ||
      k === "contact_phone" ||
      k === "personal_instagram"
    ) {
      markPersonalTouched();
    }
    if (k === "name" || k === "base_city") clearPersonalFieldError(k);
    if (k === "school_name" || k === "school_city" || k === "runs_school") {
      clearHostFieldError("school_name");
      clearHostFieldError("school_city");
    }
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function setHostsPaid(checked) {
    if (!account || savingHostsPaid) return;
    const prev = form.hosts_paid;
    setForm((f) => ({ ...f, hosts_paid: checked }));
    setSavingHostsPaid(true);
    try {
      const r = await api.patch("/organizers/me", { hosts_paid: checked });
      updateAccount(r.data.organizer);
      toast.success(checked ? "Payments enabled" : "Payments turned off");
    } catch (err) {
      setForm((f) => ({ ...f, hosts_paid: prev }));
      toast.error(formatApiError(err));
    } finally {
      setSavingHostsPaid(false);
    }
  }

  async function setRunsSchool(checked) {
    if (!account || savingRunsSchool) return;
    const prev = form.runs_school;
    const prevSchool = {
      school_name: form.school_name,
      school_city: form.school_city,
      school_city_location: form.school_city_location,
      school_bio: form.school_bio,
      school_instagram: form.school_instagram,
      school_whatsapp_url: form.school_whatsapp_url,
    };
    setForm((f) => ({
      ...f,
      runs_school: checked,
      ...(checked
        ? {}
        : {
            school_name: "",
            school_city: "",
            school_city_location: null,
            school_bio: "",
            school_instagram: "",
            school_whatsapp_url: "",
          }),
    }));
    setSavingRunsSchool(true);
    try {
      const r = await api.patch("/organizers/me", { runs_school: checked });
      updateAccount(r.data.organizer);
      if (checked) {
        setForm((f) => ({
          ...f,
          runs_school: true,
          school_name: r.data.organizer.school_name || f.school_name,
          school_city: r.data.organizer.school_city || f.school_city,
          school_city_location: r.data.organizer.school_city_location || f.school_city_location,
          school_bio: r.data.organizer.school_bio || f.school_bio,
          school_instagram: instagramHandleForInput(r.data.organizer.school_instagram) || f.school_instagram,
        }));
      }
      toast.success(checked ? "School enabled on your bio" : "School hidden from your bio");
    } catch (err) {
      setForm((f) => ({ ...f, runs_school: prev, ...prevSchool }));
      toast.error(formatApiError(err));
    } finally {
      setSavingRunsSchool(false);
    }
  }
  function updateDance(k, v) {
    markPersonalTouched();
    clearPersonalFieldError("role");
    setDanceForm((f) => ({ ...f, [k]: v }));
  }
  function updateDanceForms(dance_forms) {
    markPersonalTouched();
    clearPersonalFieldError("dance_forms");
    setDanceForm((f) => ({ ...f, dance_forms }));
  }
  function updateMini(k, v) {
    markPersonalTouched();
    setMiniProfile((mp) => ({ ...mp, [k]: v }));
  }

  function updateShowMiniOnEvents(checked) {
    markPersonalTouched();
    setShowMiniOnEvents(checked);
  }

  const hasProfile = dancerId || account;

  async function savePersonal(e) {
    e?.preventDefault();
    const errors = validatePersonalForm({ form, danceForm, account, onboarding });
    if (Object.keys(errors).length > 0) {
      setPersonalFieldErrors(errors);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => scrollToFirstProfileFieldError(errors));
      });
      return false;
    }
    setPersonalFieldErrors({});
    const trimmedName = form.name.trim();
    setSavingPersonal(true);
    try {
      if (account) {
        const orgPayload = onboarding
          ? {
              name: trimmedName,
              phone: normalizePhone(form.contact_phone) || "",
            }
          : {
              name: trimmedName,
              base_city: form.base_city.trim(),
              base_city_location: form.base_city_location,
              personal_instagram: normalizeInstagramUrl(form.personal_instagram),
              phone: normalizePhone(form.contact_phone) || "",
            };
        const r = await api.patch("/organizers/me", orgPayload);
        updateAccount(r.data.organizer);
      }

      let id = dancerId;
      if (!id) {
        id = accountLinkedDancerId(account?.id) || newDancerId();
        bindDancerId(id);
      }
      await api.patch(`/dancers/${id}`, {
        name: trimmedName,
        role: danceForm.role,
        dance_forms: danceForm.dance_forms,
        mini_profile: miniProfilePayload(miniProfile),
        show_mini_on_events: showMiniOnEvents,
        phone: normalizePhone(form.contact_phone) || null,
      });
      await refreshProfile();

      personalTouchedRef.current = false;
      setPersonalBaseline(
        serializePersonalSnapshot({
          form: {
            ...form,
            name: trimmedName,
            contact_phone: form.contact_phone,
            base_city: account ? form.base_city.trim() : form.base_city,
            personal_instagram: form.personal_instagram,
          },
          danceForm,
          miniProfile,
          showMiniOnEvents,
          account,
        })
      );

      toast.success("Profile saved 💃");
      if (onboarding) nav("/events");
      return true;
    } catch (err) {
      toast.error(formatApiError(err));
      return false;
    } finally {
      setSavingPersonal(false);
    }
  }

  async function saveHost(e) {
    e?.preventDefault();
    if (!account) return false;
    const errors = {
      ...validateHostForm({ form }),
      ...validateHostPayment(defaultPayment),
    };
    if (Object.keys(errors).length > 0) {
      setHostFieldErrors(errors);
      const sectionForError = hostSectionForErrors(errors);
      if (sectionForError !== hostSection) {
        switchHostSection(sectionForError);
      }
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollToFirstProfileFieldError(errors, PROFILE_HOST_FIELD_ORDER);
        });
      });
      return false;
    }
    setHostFieldErrors({});
    setSavingHost(true);
    try {
      const runsSchool = form.runs_school || !!(form.school_name || "").trim();
      const payload = {
        hosts_paid: form.hosts_paid,
        runs_school: runsSchool,
        default_payment: defaultPayment,
      };
      if (runsSchool) {
        payload.school_name = form.school_name.trim() || null;
        payload.school_city = form.school_city.trim() || null;
        payload.school_city_location = form.school_city_location;
        payload.school_bio = form.school_bio.trim() || null;
        payload.school_instagram = normalizeInstagramUrl(form.school_instagram);
      }
      const r = await api.patch("/organizers/me", payload);
      updateAccount(r.data.organizer);
      setHostBaseline(
        serializeHostSnapshot({
          form: {
            ...form,
            hosts_paid: form.hosts_paid,
            runs_school: runsSchool,
            school_name: form.school_name,
            school_city: form.school_city,
            school_bio: form.school_bio,
            school_instagram: form.school_instagram,
          },
          defaultPayment,
        })
      );
      toast.success("Host profile saved 🎉");
      return true;
    } catch (err) {
      toast.error(formatApiError(err));
      return false;
    } finally {
      setSavingHost(false);
    }
  }

  async function saveTheme(e) {
    e?.preventDefault();
    if (!account) return false;
    const id = normalizeHostTheme(themeDraft);
    setSavingTheme(true);
    try {
      const r = await api.patch("/organizers/me", { theme: id });
      updateAccount(r.data.organizer);
      toast.success(`${HOST_THEMES[id].name} saved`);
      return true;
    } catch (err) {
      toast.error(formatApiError(err));
      return false;
    } finally {
      setSavingTheme(false);
    }
  }

  async function deleteProfile() {
    setDeleting(true);
    try {
      if (dancerId) {
        await api.delete(`/dancers/${dancerId}`);
      }
      if (account) {
        await api.delete("/organizers/me");
        signOut();
      }
      clearGuest();
      setDanceForm({ role: "", dance_forms: [] });
      setShowDeleteConfirm(false);
      toast.success("Profile deleted.");
      nav("/");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDeleting(false);
    }
  }

  const headingName = account?.name || guestProfile?.name;

  return (
    <div className="min-h-screen bg-canvas font-body text-ink pb-[calc(4.75rem+env(safe-area-inset-bottom,0px))] sm:pb-8">
      <div className="max-w-page mx-auto px-4 pt-6 flex items-center justify-between gap-3">
        <Button variant="secondary"
          as={Link}
          to="/"
          onClick={(e) => {
            if (tabHasUnsavedEdits) {
              e.preventDefault();
              requestLeaveBack("/");
            }
          }}
          data-testid="back-link"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={2} aria-hidden /> Home
        </Button>
        {account ? (
          <Button variant="secondary" onClick={requestSignOut} data-testid="signout-btn">
            <LogOut className="w-4 h-4" strokeWidth={2} aria-hidden /> Sign out
          </Button>
        ) : (
          <Button variant="secondary" as={Link} to="/login?next=/me" data-testid="signin-btn">
            Sign in
          </Button>
        )}
      </div>

      <main className="max-w-page mx-auto px-4 mt-6">
        <p className="font-ui text-[13px] font-medium text-ink/70">
          {onboarding ? "Welcome — finish setting up" : "Your profile"}
        </p>
        <h1
          className="font-ui font-semibold text-[22px] sm:text-[24px] leading-tight text-balance mt-0.5"
          data-testid="profile-heading"
        >
          {onboarding
            ? "Set up your profile"
            : headingName
              ? `Hey, ${headingName} 👋`
              : "Your dance profile"}
        </h1>
        {onboarding && (
          <FieldHint className="mt-1.5 text-[14px]">
            Name and how you dance — enough to RSVP. Add your city anytime on your profile.
          </FieldHint>
        )}



      {!onboarding && (
        <div
          ref={tabListRef}
          className="mt-5 flex gap-2 flex-wrap"
          role="tablist"
          aria-label="Profile sections"
          aria-orientation="horizontal"
          onKeyDown={onProfileTabListKeyDown}
          data-testid="profile-tabs"
        >
          <ProfileTab
            active={tab === PROFILE_TAB.personal}
            onClick={() => requestTabSwitch(PROFILE_TAB.personal)}
            testId="profile-tab-personal"
            tabId={PROFILE_TAB_IDS.personal}
            panelId={PROFILE_PANEL_IDS.personal}
          >
            Profile
          </ProfileTab>
          <ProfileTab
            active={tab === PROFILE_TAB.nights}
            onClick={() => requestTabSwitch(PROFILE_TAB.nights)}
            testId="profile-tab-nights"
            tabId={PROFILE_TAB_IDS.nights}
            panelId={PROFILE_PANEL_IDS.nights}
          >
            RSVPs
          </ProfileTab>
          {account ? (
            <ProfileTab
              active={tab === PROFILE_TAB.host}
              onClick={() => requestTabSwitch(PROFILE_TAB.host)}
              testId="profile-tab-host"
              tabId={PROFILE_TAB_IDS.host}
              panelId={PROFILE_PANEL_IDS.host}
              className="ml-2 pl-3 border-l border-black/[0.12]"
            >
              Host
            </ProfileTab>
          ) : null}
        </div>
      )}

      {tab === PROFILE_TAB.host ? (
        <div
          className="mt-7 space-y-6"
          role="tabpanel"
          id={PROFILE_PANEL_IDS.host}
          aria-labelledby={PROFILE_TAB_IDS.host}
        >
          {!account ? (
            <Card className={`${FORM_STYLE_CARD_CLASS} text-center`}>
              <SectionTitle>Host profile</SectionTitle>
              <FieldHint className="text-[14px]">
                Sign in to manage events, school, theme, and payments.
              </FieldHint>
              <Button
                as={Link}
                to="/login?next=/me%3Ftab%3Dhost"
                variant="primary"
                className="mt-2 w-full sm:w-auto"
                data-testid="host-tab-signin-link"
              >
                Sign in to host
              </Button>
            </Card>
          ) : (
            <>
              <div
                role="tablist"
                aria-label="Host settings"
                className="flex flex-wrap gap-2"
                data-testid="profile-host-section-tabs"
              >
                <ProfileTab
                  active={hostSection === HOST_SECTION.events}
                  onClick={() => requestHostSectionSwitch(HOST_SECTION.events)}
                  testId="profile-host-section-events"
                  tabId={HOST_SECTION_IDS.events}
                  panelId={HOST_SECTION_PANEL_IDS.events}
                >
                  <span className="inline-flex items-center gap-1.5">
                    <CalendarHeart className="w-3.5 h-3.5" strokeWidth={2} aria-hidden />
                    Events
                  </span>
                </ProfileTab>
                <ProfileTab
                  active={hostSection === HOST_SECTION.school}
                  onClick={() => requestHostSectionSwitch(HOST_SECTION.school)}
                  testId="profile-host-section-school"
                  tabId={HOST_SECTION_IDS.school}
                  panelId={HOST_SECTION_PANEL_IDS.school}
                >
                  <span className="inline-flex items-center gap-1.5">
                    <Building2 className="w-3.5 h-3.5" strokeWidth={2} aria-hidden />
                    School
                  </span>
                </ProfileTab>
                <ProfileTab
                  active={hostSection === HOST_SECTION.theme}
                  onClick={() => requestHostSectionSwitch(HOST_SECTION.theme)}
                  testId="profile-host-section-theme"
                  tabId={HOST_SECTION_IDS.theme}
                  panelId={HOST_SECTION_PANEL_IDS.theme}
                >
                  <span className="inline-flex items-center gap-1.5">
                    <Palette className="w-3.5 h-3.5" strokeWidth={2} aria-hidden />
                    Theme
                  </span>
                </ProfileTab>
                <ProfileTab
                  active={hostSection === HOST_SECTION.payment}
                  onClick={() => requestHostSectionSwitch(HOST_SECTION.payment)}
                  testId="profile-host-section-payment"
                  tabId={HOST_SECTION_IDS.payment}
                  panelId={HOST_SECTION_PANEL_IDS.payment}
                >
                  <span className="inline-flex items-center gap-1.5">
                    <Wallet className="w-3.5 h-3.5" strokeWidth={2} aria-hidden />
                    Payments
                  </span>
                </ProfileTab>
              </div>

              {hostSection === HOST_SECTION.events ? (
                <div
                  role="tabpanel"
                  id={HOST_SECTION_PANEL_IDS.events}
                  aria-labelledby={HOST_SECTION_IDS.events}
                >
                  <ProfileHostEventsPanel account={account} />
                </div>
              ) : null}

              {hostSection === HOST_SECTION.theme ? (
                <form
                  onSubmit={saveTheme}
                  className="space-y-6"
                  aria-busy={savingTheme}
                  role="tabpanel"
                  id={HOST_SECTION_PANEL_IDS.theme}
                  aria-labelledby={HOST_SECTION_IDS.theme}
                >
                  <Card className={FORM_STYLE_CARD_CLASS}>
                    <SectionTitle>
                      <span className="inline-flex items-center gap-2">
                        <Palette className="w-4 h-4" strokeWidth={2} aria-hidden />
                        Theme
                      </span>
                    </SectionTitle>
                    <FieldHint className="text-[14px]">
                      Applies to your public host profile, school, events, and classes. Guests always see your theme — not theirs. Tap a theme to preview.
                    </FieldHint>
                    <div
                      className="mt-5 grid gap-3 sm:grid-cols-2"
                      role="radiogroup"
                      aria-label="Host page themes"
                      data-testid="theme-picker"
                    >
                      {HOST_THEME_IDS.map((id) => {
                        const meta = HOST_THEMES[id];
                        const selected = themeDraft === id;
                        const { canvas, ink, muted, cta } = meta.preview;
                        return (
                          <button
                            key={id}
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            data-testid={`theme-option-${id}`}
                            onClick={() => {
                              setThemeDraft(id);
                              setThemePreviewOpen(true);
                            }}
                            className={`text-left rounded-lg2 border p-3.5 transition-[box-shadow,border-color] focus-visible:outline focus-visible:outline-2 focus-visible:outline-ink/30 focus-visible:outline-offset-2 ${
                              selected ? "ring-2 ring-offset-2 ring-offset-canvas" : ""
                            }`}
                            style={{
                              background: canvas,
                              color: ink,
                              borderColor: selected ? cta : meta.dark ? "rgba(255,255,255,0.14)" : "rgba(0,0,0,0.1)",
                              ...(selected ? { boxShadow: `0 0 0 1px ${cta}`, "--tw-ring-color": cta } : null),
                            }}
                          >
                            <span
                              className="flex h-10 rounded-md overflow-hidden border"
                              style={{
                                borderColor: meta.dark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)",
                              }}
                              aria-hidden
                            >
                              <span className="flex-1" style={{ background: canvas }} />
                              <span className="w-8" style={{ background: cta }} />
                              <span className="w-6" style={{ background: ink }} />
                            </span>
                            <span className="mt-2.5 block font-ui font-semibold text-[14px]" style={{ color: ink }}>
                              {meta.name}
                              {id === DEFAULT_HOST_THEME ? (
                                <span className="ml-1.5 font-medium" style={{ color: muted }}>
                                  · default
                                </span>
                              ) : null}
                            </span>
                            <span
                              className="mt-0.5 block font-ui text-[12px] leading-snug"
                              style={{ color: muted }}
                            >
                              {meta.description}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </Card>

                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => setThemePreviewOpen(true)}
                      data-testid="theme-preview-btn"
                    >
                      Preview
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={savingTheme || !themeDirty}
                      data-testid="theme-save-btn"
                    >
                      {savingTheme ? "Saving…" : "Save"}
                    </Button>
                  </div>
                </form>
              ) : null}

              {hostSection === HOST_SECTION.payment ? (
                <div
                  role="tabpanel"
                  id={HOST_SECTION_PANEL_IDS.payment}
                  aria-labelledby={HOST_SECTION_IDS.payment}
                  className="space-y-6"
                >
                  {!form.hosts_paid ? (
                    <EmptyState
                      compact
                      icon={<Wallet className="w-8 h-8" strokeWidth={1.5} aria-hidden />}
                      title="Accept payments from guests"
                      description="Add a payment link or UPI so guests can pay for your events and classes."
                      action={
                        <Button
                          type="button"
                          variant="primary"
                          disabled={savingHostsPaid}
                          onClick={() => setHostsPaid(true)}
                          data-testid="enable-payments-empty-btn"
                        >
                          {savingHostsPaid ? "Enabling…" : "Set up payments"}
                        </Button>
                      }
                      data-testid="profile-payment-empty"
                    />
                  ) : (
                    <form id={PROFILE_HOST_FORM_ID} onSubmit={saveHost} className="space-y-6" aria-busy={savingHost}>
                      <FormValidationAlert count={Object.keys(hostFieldErrors).length} />
                      <Card className={FORM_STYLE_CARD_CLASS}>
                        <SectionTitle icon={Wallet}>Default Payment Method</SectionTitle>
                        <FieldHint className="text-[12px] sm:text-[13px]">
                          Pre-fill payment details when you create an event. Shown on your profile and events.
                        </FieldHint>
                        <SelfPaymentForm
                          values={defaultPayment}
                          onChange={(next) => {
                            setHostFieldErrors((prev) => {
                              if (!prev.contact_phone && !prev.contact_email) return prev;
                              const updated = { ...prev };
                              delete updated.contact_phone;
                              delete updated.contact_email;
                              return updated;
                            });
                            setDefaultPayment(next);
                          }}
                          payeeName={form.name}
                          idPrefix="profile-payment"
                          fieldErrors={hostFieldErrors}
                        />
                      </Card>
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2 border-t border-black/[0.08]">
                        <Button
                          type="button"
                          variant="secondary"
                          disabled={savingHostsPaid}
                          onClick={() => setHostsPaid(false)}
                          data-testid="disable-payments-btn"
                        >
                          {savingHostsPaid ? "Updating…" : "Turn off payments"}
                        </Button>
                        <Button type="submit" variant="primary" disabled={savingHost} data-testid="save-host-profile-btn" className="hidden sm:inline-flex">
                          {savingHost ? "Saving…" : "Save payment setup"}
                        </Button>
                      </div>
                    </form>
                  )}
                </div>
              ) : null}

              {hostSection === HOST_SECTION.school ? (
                <div
                  role="tabpanel"
                  id={HOST_SECTION_PANEL_IDS.school}
                  aria-labelledby={HOST_SECTION_IDS.school}
                  className="space-y-6"
                >
                  {!form.runs_school ? (
                    <EmptyState
                      compact
                      icon={<Building2 className="w-8 h-8" strokeWidth={1.5} aria-hidden />}
                      title="Set up your dance school"
                      description="Add school details for your public host bio and class admin tools."
                      action={
                        <Button
                          type="button"
                          variant="primary"
                          disabled={savingRunsSchool}
                          onClick={() => setRunsSchool(true)}
                          data-testid="enable-school-empty-btn"
                        >
                          {savingRunsSchool ? "Enabling…" : "Set up school"}
                        </Button>
                      }
                      data-testid="profile-school-empty"
                    />
                  ) : (
                    <form id={PROFILE_HOST_FORM_ID} onSubmit={saveHost} className="space-y-6" aria-busy={savingHost}>
                      <FormValidationAlert count={Object.keys(hostFieldErrors).length} />
                      <Card className={FORM_STYLE_CARD_CLASS}>
                        <SectionTitle icon={Building2}>Dance school</SectionTitle>
                        <div className="space-y-4">
                          <HostSchoolSetup
                            enabled
                            schoolName={form.school_name}
                            schoolBio={form.school_bio}
                            schoolCity={form.school_city}
                            schoolInstagram={form.school_instagram}
                            whatsappUrl={form.school_whatsapp_url}
                            onSchoolLoaded={(linked) => {
                              setForm((f) => {
                                const next = { ...f };
                                if (!f.school_name && linked.name) next.school_name = linked.name;
                                if (!f.school_city && linked.city) next.school_city = linked.city;
                                if (!f.school_bio && linked.description) next.school_bio = linked.description;
                                if (!f.school_instagram && linked.instagram) {
                                  next.school_instagram = linked.instagram;
                                }
                                if (!f.school_whatsapp_url && linked.whatsapp_url) {
                                  next.school_whatsapp_url = linked.whatsapp_url;
                                }
                                return next;
                              });
                            }}
                          />
                          <SchoolProfileForm
                            values={{
                              name: form.school_name,
                              city: form.school_city,
                              city_location: form.school_city_location,
                              bio: form.school_bio,
                              instagram: form.school_instagram,
                              whatsapp_url: form.school_whatsapp_url || "",
                            }}
                            errors={{
                              name: hostFieldErrors.school_name,
                              city: hostFieldErrors.school_city,
                            }}
                            cityPlaceholder={locale.default_city || "Berlin"}
                            onChange={(next) => {
                              setForm((f) => ({
                                ...f,
                                school_name: next.name,
                                school_city: next.city,
                                school_city_location: next.city_location,
                                school_bio: next.bio,
                                school_instagram: next.instagram,
                                school_whatsapp_url: next.whatsapp_url,
                              }));
                              if (next.name !== form.school_name) clearHostFieldError("school_name");
                              if (next.city !== form.school_city) clearHostFieldError("school_city");
                            }}
                          />
                        </div>
                      </Card>
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2 border-t border-black/[0.08]">
                        <Button
                          type="button"
                          variant="secondary"
                          disabled={savingRunsSchool}
                          onClick={() => setRunsSchool(false)}
                          data-testid="disable-school-btn"
                        >
                          {savingRunsSchool ? "Updating…" : "Hide school from bio"}
                        </Button>
                        <Button type="submit" variant="primary" disabled={savingHost} data-testid="save-school-profile-btn" className="hidden sm:inline-flex">
                          {savingHost ? "Saving…" : "Save school"}
                        </Button>
                      </div>
                    </form>
                  )}
                </div>
              ) : null}
            </>
          )}
        </div>
      ) : tab === PROFILE_TAB.nights ? (
        <div
          className="mt-7 space-y-6"
          role="tabpanel"
          id={PROFILE_PANEL_IDS.nights}
          aria-labelledby={PROFILE_TAB_IDS.nights}
        >
          {appreciation && (
            <Card className={FORM_STYLE_CARD_CLASS} data-testid="profile-appreciation-section">
              <SectionTitle icon={Sparkles}>Appreciation</SectionTitle>
              <p
                className="font-ui font-semibold text-[18px] sm:text-[20px] leading-snug"
                aria-live="polite"
              >
                You&apos;ve been picked as a favorite dance at{" "}
                <span className="tabular-nums">{appreciation.socials_this_season}</span>{" "}
                social{appreciation.socials_this_season === 1 ? "" : "s"} this {appreciation.season}
              </p>
              <FieldHint className="text-[12px] sm:text-[13px]">
                Private picks from other dancers — no names, scores, or rankings.
              </FieldHint>
            </Card>
          )}

          <Card className={FORM_STYLE_CARD_CLASS} data-testid="profile-events-section">
            <SectionTitle icon={CalendarHeart}>Regular spots</SectionTitle>
            <FieldHint className="text-[12px] sm:text-[13px]">
              Recurring socials you add when you RSVP. Only you see this for now.
            </FieldHint>
            {loadingProfileEvents ? (
              <ProfileLoadingSkeleton label="Loading your spots…" variant="list" />
            ) : profileEvents.length === 0 ? (
              <FieldHint className="text-[14px]">
                No regular spots yet. When you RSVP to a recurring social, tick{" "}
                <span className="font-semibold text-ink">Add to my profile</span> on the RSVP form.
              </FieldHint>
            ) : (
              <ul className="space-y-2">
                {profileEvents.map((item) => {
                  const ev = item.event;
                  const when = item.next_occurrence?.starts_at
                    ? new Date(item.next_occurrence.starts_at).toLocaleString("en-GB", {
                        weekday: "short",
                        day: "numeric",
                        month: "short",
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                    : null;
                  return (
                    <li key={item.event_id}>
                      <Link
                        to={ev.public_path || eventPublicPath(ev) || "/events"}
                        className={PROFILE_LIST_LINK}
                        data-testid={`profile-event-${item.event_id}`}
                      >
                        <span className="truncate">{ev.name}</span>
                        <span className="text-[12px] font-medium text-ink/70 shrink-0">
                          {when ? `Next: ${when}` : ev.city || "Recurring"}
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>

          <Card className={FORM_STYLE_CARD_CLASS} data-testid="profile-past-events-section">
            <SectionTitle icon={History}>Past parties</SectionTitle>
            {loadingPastEvents ? (
              <ProfileLoadingSkeleton label="Loading past parties…" variant="list" />
            ) : pastEvents.length === 0 ? (
              <FieldHint className="text-[14px]">
                No past parties yet. RSVP to a social — it&apos;ll land here after the night.
              </FieldHint>
            ) : (
              <ul className="space-y-2">
                {pastEvents.map((item) => {
                  const ev = item.event;
                  const when = item.occurrence_at
                    ? new Date(item.occurrence_at).toLocaleString("en-GB", {
                        weekday: "short",
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                    : null;
                  return (
                    <li key={item.id}>
                      <Link
                        to={`${ev.public_path || eventPublicPath(ev) || "/events"}${item.occurrence_date ? `?occurrence_date=${item.occurrence_date}` : ""}`}
                        className={PROFILE_LIST_LINK}
                        data-testid={`profile-past-event-${item.id}`}
                      >
                        <span className="truncate">{ev.name}</span>
                        <span className="text-[12px] font-medium text-ink/70 shrink-0 text-right">
                          {when}
                          {item.my_rsvp && (
                            <span className="block">{attendanceLabel(item.my_rsvp.attendance_status)}</span>
                          )}
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>
        </div>
      ) : tab === PROFILE_TAB.personal || onboarding ? (
        <div
          className="mt-7"
          role="tabpanel"
          id={onboarding ? undefined : PROFILE_PANEL_IDS.personal}
          aria-labelledby={onboarding ? undefined : PROFILE_TAB_IDS.personal}
        >
        {onboarding ? (
          <ProfileOnboardingWizard
            name={form.name}
            onNameChange={(v) => update("name", v)}
            danceForm={danceForm}
            onRoleChange={(v) => updateDance("role", v)}
            onDanceFormsChange={(dance_forms) => {
              updateDanceForms(dance_forms);
            }}
            fieldErrors={personalFieldErrors}
            onSetFieldErrors={setPersonalFieldErrors}
            onClearFieldError={clearPersonalFieldError}
            loadingDance={loadingDance}
            saving={savingPersonal}
            dirty={personalDirty}
            account={account}
            onSave={savePersonal}
          />
        ) : (
        <>
        <form
          id={PROFILE_PERSONAL_FORM_ID}
          onSubmit={savePersonal}
          className="space-y-6"
          aria-busy={savingPersonal || loadingDance}
        >
          <FormValidationAlert count={Object.keys(personalFieldErrors).length} />

          {dancerId && (
            <div className="mb-2 space-y-4">
              <NotificationSettings dancerId={dancerId} />
              <SchoolNotificationsSection />
            </div>
          )}

          <Card className={FORM_STYLE_CARD_CLASS}>
            <ThumbnailUploadField
              dancerId={dancerId}
              account={account}
              guestThumbnailUrl={guestProfile?.thumbnail_url}
              organizerThumbnailUrl={account?.thumbnail_url}
              role={danceForm.role}
              name={account?.name || form.name || guestProfile?.name}
              onDancerUpdated={() => refreshProfile()}
              onOrganizerUpdated={updateAccount}
            />

            <div className="mt-5 pt-5 border-t border-black/[0.08]">
            <SectionTitle icon={User}>About you</SectionTitle>
            <FormTextField
              id={PROFILE_FIELD_IDS.name}
              label="Name"
              required
              error={personalFieldErrors.name}
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              placeholder="Marta"
              autoComplete="name"
              maxLength={100}
              data-testid="name-input"
            />
            {account && (
              <label className="block">
                <FormLabel className="block mb-2">Home city *</FormLabel>
                <CityAutocomplete
                  value={form.base_city}
                  onChange={(v) => {
                    update("base_city", v);
                    if (v !== form.base_city) updateBaseCityLocation(null);
                  }}
                  onResolvedChange={updateBaseCityLocation}
                  placeholder={locale.default_city || "Berlin"}
                  testId="home-city-input"
                  inputId={PROFILE_FIELD_IDS.base_city}
                  error={personalFieldErrors.base_city}
                  required
                />
              </label>
            )}
            </div>
          </Card>

          <Card className={FORM_STYLE_CARD_CLASS}>
            <SectionTitle icon={Phone}>Get in touch</SectionTitle>
            <FieldHint className="text-[12px] sm:text-[13px]">
              Optional WhatsApp number — hosts use it to confirm and message you.
            </FieldHint>
            <ContactPhoneField
              className="mt-4"
              id={PROFILE_FIELD_IDS.contact_phone}
              value={form.contact_phone}
              onChange={(v) => {
                clearPersonalFieldError("contact_phone");
                update("contact_phone", v);
              }}
              error={personalFieldErrors.contact_phone}
              errorId={`${PROFILE_FIELD_IDS.contact_phone}-error`}
              testId="contact-phone-input"
            />
            {account && (
              <label className="block">
                <FormLabel className="block mb-2 flex items-center gap-1">
                  <Instagram className="w-3.5 h-3.5" strokeWidth={2} aria-hidden /> Instagram{" "}
                  <span className="font-normal text-ink/60">(optional)</span>
                </FormLabel>
                <InstagramInput
                  id="personal-instagram-input"
                  value={form.personal_instagram}
                  onChange={(v) => update("personal_instagram", v)}
                  placeholder="yourhandle"
                  data-testid="personal-instagram-input"
                />
              </label>
            )}
          </Card>

          <Card className={FORM_STYLE_CARD_CLASS}>
            <SectionTitle icon={Music2}>How do you dance?</SectionTitle>
            <FieldHint className="text-[12px] sm:text-[13px]">
              Hosts see this on your RSVP. Favorite Dances stats and notes are on{" "}
              <button
                type="button"
                onClick={openDanceNightsTab}
                className="underline text-plum font-semibold"
              >
                RSVPs
              </button>
              .
            </FieldHint>

            {loadingDance ? (
              <div className="animate-pulse space-y-3" aria-busy="true" aria-label="Loading dance profile">
                <div className="h-10 w-56 max-w-full rounded-md2 bg-black/[0.06]" />
                <div className="h-10 w-full rounded-md2 bg-black/[0.06]" />
                <p className="sr-only">Loading dance profile…</p>
              </div>
            ) : (
              <>
                <DanceRoleField
                  id={PROFILE_FIELD_IDS.role}
                  required
                  value={danceForm.role}
                  onChange={(v) => updateDance("role", v)}
                  testIdPrefix="profile-role"
                  error={personalFieldErrors.role}
                  errorId={`${PROFILE_FIELD_IDS.role}-error`}
                />

                <DanceFormsField
                  id={PROFILE_FIELD_IDS.dance_forms}
                  value={danceForm.dance_forms}
                  onChange={updateDanceForms}
                  error={personalFieldErrors.dance_forms}
                  errorId={`${PROFILE_FIELD_IDS.dance_forms}-error`}
                  showLevelsSection
                />
              </>
            )}
          </Card>

          {!loadingDance && (
            <Card className={FORM_STYLE_CARD_CLASS} data-testid="make-it-yours-section">
              <details className="group">
                <summary className="list-none [&::-webkit-details-marker]:hidden cursor-pointer flex items-center justify-between gap-3 min-h-[44px] rounded-md2 -mx-1 px-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ink/30 focus-visible:outline-offset-2">
                  <span className="min-w-0">
                    <SectionTitle icon={Sparkles} className="mb-0">
                      Make it yours
                    </SectionTitle>
                    <FieldHint className="text-[12px] sm:text-[13px] mt-1">
                      Optional — guests see this when they tap your name on event pages.
                    </FieldHint>
                  </span>
                  <ChevronDown
                    className="w-5 h-5 shrink-0 text-ink/40 transition-transform motion-reduce:transition-none group-open:rotate-180"
                    strokeWidth={2}
                    aria-hidden
                  />
                </summary>
                <div className="mt-4 space-y-4">
                  <label className="block">
                    <FormLabel className="block mb-2">About me</FormLabel>
                    <input
                      className="field"
                      value={miniProfile.about_me}
                      onChange={(e) => updateMini("about_me", e.target.value)}
                      placeholder="One line. Make it count."
                      data-testid="profile-about-me-input"
                    />
                  </label>
                  <label className="block">
                    <FormLabel className="block mb-2 flex items-center gap-1">
                      <Music2 className="w-3.5 h-3.5" strokeWidth={2} aria-hidden /> Favourite songs
                    </FormLabel>
                    <input
                      className="field"
                      value={miniProfile.favourite_songs}
                      onChange={(e) => updateMini("favourite_songs", e.target.value)}
                      placeholder="Obsesión · Propuesta Indecente · …"
                      data-testid="profile-fav-songs-input"
                    />
                  </label>
                  <label className="block">
                    <FormLabel className="block mb-2 flex items-center gap-1">
                      <Heart className="w-3.5 h-3.5" strokeWidth={2} aria-hidden /> Relationship status
                    </FormLabel>
                    <input
                      className="field"
                      value={miniProfile.relationship_status}
                      onChange={(e) => updateMini("relationship_status", e.target.value)}
                      placeholder="Single · Taken · It's complicated…"
                      data-testid="profile-status-input"
                    />
                  </label>
                  <CheckboxField
                    id="show-mini-on-events-checkbox"
                    checked={showMiniOnEvents}
                    onChange={(e) => updateShowMiniOnEvents(e.target.checked)}
                    title="Show this on event pages"
                    hint="You can turn this off per event when you RSVP."
                    testId="show-mini-on-events-checkbox"
                  />
                </div>
              </details>
            </Card>
          )}

          <div className="hidden sm:flex justify-end pt-2 border-t border-black/[0.08]">
            <Button type="submit" variant="primary" disabled={savingPersonal} data-testid="save-profile-btn">
              {savingPersonal ? "Saving…" : onboarding ? "Save & continue →" : "Save profile"}
            </Button>
          </div>
        </form>

        {hasProfile && !onboarding && (
          <div className="mt-8">
            <Card className={FORM_STYLE_CARD_CLASS}>
              <SectionTitle className="text-ink/70">Danger zone</SectionTitle>
              {!showDeleteConfirm ? (
                <Button
                  type="button"
                  variant="dangerOutline"
                  onClick={() => setShowDeleteConfirm(true)}
                  data-testid="delete-profile-btn"
                >
                  <Trash2 className="w-4 h-4" strokeWidth={2} aria-hidden /> Delete profile
                </Button>
              ) : (
                <div className="space-y-3">
                  <FieldHint className="text-[14px]">
                    {account
                      ? "This permanently deletes your host account, dance profile, and all RSVPs. This can't be undone."
                      : "This permanently deletes your dance profile and all RSVPs. This can't be undone."}
                  </FieldHint>
                  <div className="flex gap-2 flex-wrap">
                    <Button variant="secondary" type="button" onClick={() => setShowDeleteConfirm(false)} disabled={deleting} data-testid="cancel-delete-btn">
                      Keep profile
                    </Button>
                    <Button
                      type="button"
                      variant="danger"
                      onClick={deleteProfile}
                      disabled={deleting}
                      data-testid="confirm-delete-btn"
                    >
                      {deleting ? "Deleting…" : "Delete my profile"}
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          </div>
        )}
        </>
        )}
        </div>
      ) : null}

      </main>

      {((tab === PROFILE_TAB.personal)
        || (tab === PROFILE_TAB.host && account && isHostSettingsSection(hostSection) && (
          (hostSection === HOST_SECTION.payment && form.hosts_paid)
          || (hostSection === HOST_SECTION.school && form.runs_school)
        ))) && !onboarding && (
        <ProfileStickySaveBar
          formId={tab === PROFILE_TAB.host ? PROFILE_HOST_FORM_ID : PROFILE_PERSONAL_FORM_ID}
          saving={tab === PROFILE_TAB.host ? savingHost : savingPersonal}
          dirty={tab === PROFILE_TAB.host ? hostDirty : personalDirty}
          label={
            hostSection === HOST_SECTION.payment
              ? "Save payment setup"
              : hostSection === HOST_SECTION.school
                ? "Save school"
                : "Save profile"
          }
          secondaryLink={
            tab === PROFILE_TAB.host && isHostSettingsSection(hostSection)
              ? {
                  label: "Events →",
                  testId: "sticky-my-events-link",
                  onNavigate: () => leaveViaLink("/me?tab=host&section=events"),
                }
              : undefined
          }
          submitTestId={
            tab === PROFILE_TAB.host ? "sticky-save-host-profile-btn" : "sticky-save-profile-btn"
          }
        />
      )}


      <ThemePreviewDrawer
        open={themePreviewOpen && tab === PROFILE_TAB.host && isThemeSection(hostSection)}
        onClose={() => setThemePreviewOpen(false)}
        organizerId={account?.id}
        themeId={themeDraft}
        fallbackHost={account || null}
      />

      <ConfirmLeaveModal
        open={Boolean(pendingLeave)}
        busy={savingPersonal || savingHost || savingTheme}
        onCancel={() => setPendingLeave(null)}
        onConfirm={confirmLeave}
        title="Leave without saving?"
        body="You have unsaved edits on this tab. Discard them, or save before you continue."
        checkboxLabel="Save changes before you continue"
        cancelLabel="Cancel"
        saveLabel="Save"
        discardLabel="Discard"
        testId="profile-unsaved-modal"
      />
    </div>
  );
}
